import React, { useCallback, useContext, useMemo, useState } from 'react';
import { SalesHistoryContext } from '../SalesHistoryContext';
import { useProducts } from '../ProductContext';
import { usePrinting } from '../PrintingContext';
import { useCash } from '../CashContext';
import { useOtherServiceSales } from '../OtherServiceSalesContext';
import { apiJson } from '../apiClient';
import { useLanguage } from '../i18n/LanguageContext';
import { brandingFromUser } from '../utils/tenantBranding';
import { buildLoanPackSnapshot } from '../utils/loanPackData';
import { exportLoanPackPdf } from '../utils/loanPackExport';
import { isBrickFactoryUser } from '../utils/businessType';
import { hasTenantModule } from '../utils/tenantModules';
import { canModerateBranch } from '../utils/userLocale';
import './ReportPages.css';
import ReportFooter from '../components/ReportFooter';

function tsh(n) {
  return `TZS ${Math.round(Number(n) || 0).toLocaleString('en-TZ')}`;
}

const LoanPackPage = ({ user, onNavigate }) => {
  const { t } = useLanguage();
  const { salesHistory } = useContext(SalesHistoryContext);
  const { products } = useProducts();
  const { printJobs } = usePrinting();
  const { transactions } = useCash();
  const { sales: otherServiceSales } = useOtherServiceSales();

  const [monthCount, setMonthCount] = useState(6);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snapshot, setSnapshot] = useState(null);

  const canAccess = canModerateBranch(user?.role);

  const fetchSnapshot = useCallback(async () => {
    if (!canAccess) return null;
    try {
      const monthKeys = [];
      const d = new Date();
      for (let i = 0; i < monthCount; i += 1) {
        const yy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        monthKeys.unshift(`${yy}-${mm}`);
        d.setMonth(d.getMonth() - 1);
      }

      const tasks = [apiJson('/expenses').catch(() => ({ expenses: [] }))];
      if (hasTenantModule(user, 'debts')) {
        tasks.push(apiJson('/customer-debts?status=open').catch(() => ({ summary: {} })));
      }

      const factoryByMonth = {};
      if (isBrickFactoryUser(user) && hasTenantModule(user, 'brick')) {
        const factoryReports = await Promise.all(
          monthKeys.map((m) =>
            apiJson(`/brick/report?window=month&month=${m}`).catch(() => null)
          )
        );
        monthKeys.forEach((m, i) => {
          if (factoryReports[i]) factoryByMonth[m] = factoryReports[i];
        });
      }

      const results = await Promise.all(tasks);
      const expenseRes = results[0];
      const debtRes = hasTenantModule(user, 'debts') ? results[1] : null;

      const data = buildLoanPackSnapshot({
        monthCount,
        salesHistory,
        expenses: expenseRes?.expenses || [],
        products,
        printJobs,
        otherServiceSales,
        transactions,
        factoryByMonth,
        debtSummary: debtRes?.summary || { openCount: 0, totalOwedTzs: 0 },
        user,
      });
      return data;
    } catch (e) {
      throw new Error(e.message || t('loanPack.loadError'));
    }
  }, [
    canAccess,
    monthCount,
    salesHistory,
    products,
    printJobs,
    otherServiceSales,
    transactions,
    user,
    t,
  ]);

  const loadSnapshot = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchSnapshot();
      setSnapshot(data);
    } catch (e) {
      setSnapshot(null);
      setError(e.message || t('loanPack.loadError'));
    } finally {
      setLoading(false);
    }
  }, [fetchSnapshot, t]);

  const pdfLabels = useMemo(
    () => ({
      branding: brandingFromUser(user),
      title: t('loanPack.pdfTitle'),
      period: t('loanPack.period'),
      generated: t('loanPack.generated'),
      phone: t('loanPack.phone'),
      executiveSummary: t('loanPack.executiveSummary'),
      totalRevenue: t('loanPack.totalRevenue'),
      posRevenue: t('loanPack.posRevenue'),
      posProfit: t('loanPack.posProfit'),
      totalExpenses: t('loanPack.totalExpenses'),
      netEstimate: t('loanPack.netEstimate'),
      stockValue: t('loanPack.stockValue'),
      stockProducts: t('loanPack.stockProducts'),
      receivables: t('loanPack.receivables'),
      openDebts: t('loanPack.openDebts'),
      bricksNet: t('loanPack.bricksNet'),
      deliveryRevenue: t('loanPack.deliveryRevenue'),
      printCollected: t('loanPack.printCollected'),
      monthlyBreakdown: t('loanPack.monthlyBreakdown'),
      month: t('loanPack.month'),
      expenses: t('loanPack.expenses'),
      total: t('loanPack.total'),
      factorySection: t('loanPack.factorySection'),
      deliveryPieces: t('loanPack.deliveryPieces'),
      expenseSplit: t('loanPack.expenseSplit'),
      category: t('loanPack.category'),
      amount: t('loanPack.amount'),
      expenseBoss: t('loanPack.expenseBoss'),
      expenseStaff: t('loanPack.expenseStaff'),
      expenseOffice: t('loanPack.expenseOffice'),
      disclaimer: t('loanPack.disclaimer'),
    }),
    [user, t]
  );

  const handleExport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let data = snapshot;
      if (!data) {
        data = await fetchSnapshot();
        setSnapshot(data);
      }
      await exportLoanPackPdf(data, user, pdfLabels);
    } catch (e) {
      setError(e.message || t('loanPack.exportError'));
    } finally {
      setLoading(false);
    }
  }, [snapshot, fetchSnapshot, user, pdfLabels, t]);

  if (!canAccess) {
    return (
      <div className="report-shell">
        <p className="muted">{t('loanPack.accessDenied')}</p>
      </div>
    );
  }

  return (
    <div className="report-shell">
      <div className="report-back">
        <button type="button" onClick={() => onNavigate('reports')}>
          ← {t('loanPack.back')}
        </button>
      </div>
      <div className="report-title-center">
        <h1>{t('loanPack.title')}</h1>
      </div>
      <p className="muted">{t('loanPack.lead')}</p>

      <div className="report-toolbar">
        <div className="field">
          <label>{t('loanPack.monthsLabel')}</label>
          <select value={monthCount} onChange={(e) => setMonthCount(Number(e.target.value))}>
            <option value={6}>{t('loanPack.months6')}</option>
            <option value={12}>{t('loanPack.months12')}</option>
          </select>
        </div>
        <div className="report-actions">
          <button type="button" disabled={loading} onClick={() => void loadSnapshot()}>
            {loading ? t('loanPack.loading') : t('loanPack.preview')}
          </button>
          <button type="button" disabled={loading} onClick={() => void handleExport()}>
            {t('loanPack.downloadPdf')}
          </button>
        </div>
      </div>

      {error ? <p className="factory-report-err">{error}</p> : null}

      {snapshot ? (
        <>
          <div className="report-grid-cards">
            <div className="report-stat">
              <span>{t('loanPack.totalRevenue')}</span>
              <strong>{tsh(snapshot.totals.totalRevenue)}</strong>
            </div>
            <div className="report-stat">
              <span>{t('loanPack.totalExpenses')}</span>
              <strong>{tsh(snapshot.totals.expenses)}</strong>
            </div>
            <div className="report-stat">
              <span>{t('loanPack.netEstimate')}</span>
              <strong>{tsh(snapshot.totals.netEstimate)}</strong>
            </div>
            <div className="report-stat">
              <span>{t('loanPack.stockValue')}</span>
              <strong>{tsh(snapshot.stock.stockValue)}</strong>
            </div>
            {snapshot.flags.showDebts ? (
              <div className="report-stat">
                <span>{t('loanPack.receivables')}</span>
                <strong>{tsh(snapshot.debts.totalOwedTzs)}</strong>
              </div>
            ) : null}
          </div>
          <p className="muted" style={{ textAlign: 'center', marginTop: 8 }}>
            {t('loanPack.period')}: <strong>{snapshot.periodLabel}</strong>
          </p>
        </>
      ) : null}

      <ReportFooter />
    </div>
  );
};

export default LoanPackPage;
